<template>
    <loading-indicator v-if="isLoading" auto-height />
    <!-- TODO improvement: Add filter for types/subjects (e.g. commission splits on contract etc.) as well as description (created/updated/deleted) -->
    <div v-else class="activity-log-wrapper">
        <ul v-if="activityLogs.length > 0" class="activity-log-list">
            <li
                v-for="activityLog in activityLogs"
                :key="activityLog.id"
                :class="['activity-log-item', { 'new-date': activityLog.displayDate }]"
                :data-activity-log-log-name="activityLog.log_name"
                :data-activity-log-description="activityLog.description"
            >
                <div class="activity-log-timestamp">
                    <span v-if="activityLog.displayDate" class="date" aria-hidden="true">{{ activityLog.formattedCreatedAtDate }}</span>
                    <time :datetime="activityLog.created_at" class="time">{{ activityLog.formattedCreatedAtTime }}</time>
                </div>

                <div class="activity-log-content">
                    <!-- TODO improvement: Indicate if causer is the user himself or a user from another consulting company? -->
                    <p class="activity-log-description" v-html="DOMPurify.sanitize(activityLog.data.logDescription)" />
                    <ul
                        v-if="$can('ActivityLog:read:details') && activityLog.data.logDetails.length"
                        class="activity-log-details-list"
                    >
                        <li v-for="logDetail in activityLog.data.logDetails" :key="logDetail.key">
                            <span v-if="logDetail.label" class="label">{{ logDetail.label }}: </span>
                            <del v-if="logDetail.oldValue !== undefined" class="old-value" v-html="DOMPurify.sanitize(logDetail.oldValue)" />
                            <span v-if="logDetail.oldValue !== undefined && logDetail.newValue !== undefined"> → </span>
                            <ins v-if="logDetail.newValue !== undefined" class="new-value" v-html="DOMPurify.sanitize(logDetail.newValue)" />
                        </li>
                    </ul>
                </div>
            </li>
        </ul>
        <!-- No activity log entries available -->
        <info-box v-else no-margin>
            <p>{{ $tc('common.change-log.no-changes-logged-yet', 1) }}</p>
        </info-box>
    </div>
</template>

<script>
import { ActivityLogService } from '@/services'
import { getContactAddress } from '@/helpers/contact'
import { ContactType } from '@/enums/graphql'
import { kebabCase, upperFirst } from 'lodash'
import DOMPurify from 'dompurify'

export default {
    name: 'ActivityLog',
    props: {
        filterSubjects: {
            type: Array,
            required: true,
        },
        filterIncludeSubjectRelations: {
            type: Array,
        },
    },
    data () {
        return {
            ContactType,
            DOMPurify,
            isLoading: false,
            activityLogs: [],
        }
    },
    created () {
        this.fetchObjects()
    },
    methods: {
        fetchObjects () {
            this.isLoading = true
            const variables = {
                filterSubjects: this.filterSubjects,
                filterIncludeSubjectRelations: this.filterIncludeSubjectRelations,
            }
            ActivityLogService.all(variables).then(response => {
                let currentDate = null
                const activityLogs = response.data.map(activityLog => {
                    if (activityLog.formattedCreatedAtDate !== currentDate) {
                        activityLog.displayDate = true
                        currentDate = activityLog.formattedCreatedAtDate
                    }
                    if (variables.filterSubjects && variables.filterSubjects.length && variables.filterIncludeSubjectRelations && variables.filterIncludeSubjectRelations.length) {
                        if (variables.filterSubjects[0].type.toLowerCase() !== activityLog.log_name.toLowerCase()) activityLog.isSubjectRelation = true
                    }
                    return activityLog
                })
                this.activityLogs = activityLogs
                this.isLoading = false
            })
        },
        getContactAddress (...args) {
            return getContactAddress(...args)
        },
        kebabCase: function (text) {
            return kebabCase(text)
        },
    },
}
</script>

<style lang="scss" scoped>
$sizeListIndent: 140px;

.activity-log-wrapper {
}

.activity-log-list {
    padding: 0;
}

.activity-log-item {
    display: flex;
    margin-bottom: 1rem;

    &::before {
        display: none;
    }

    &.new-date {
        padding-top: $sizeSpacingSm;
        border-top: 1px solid var(--color-border-primary);
    }

    p {
        margin-bottom: $sizeSpacingXs;
    }
}

.activity-log-timestamp {
    display: flex;
    flex: 0 0 $sizeListIndent;
    justify-content: space-between;

    margin-right: $sizeSpacingSm;

    color: var(--color-text-secondary);

    .date {
        font-weight: bold;
        color: var(--color-text-primary);
    }

    .time {
        flex: 1 0;
        text-align: right;
    }

    .action-icon {
        margin-left: 0.5em;
    }
}

.activity-log-content {
    ::v-deep {
        del,
        ins {
            address {
                text-decoration: inherit;
                text-decoration-color: inherit;
            }
        }
    }
}

.activity-log-description {
    &:first-letter {
        // This is just cosmetics to not add unnecessary complexity in JS for some edge cases where something might need to be capitalized in some languages but not in others.
        // Also: Same argumentation for some cases where no `causer` exists and thus sometimes something might have to be capitalized only then.
        text-transform: capitalize;
    }
}

.activity-log-details-list {
    [class*="-value"] {
        vertical-align: top;
        white-space: pre-wrap;
    }

    .label {
        vertical-align: top;
    }

    ins,
    del {
        word-break: break-word;
    }
}

@media print {
    .activity-log-item {
        page-break-inside: avoid;
    }
}
</style>
